
import type { LexicalEditor } from 'lexical';
import { createEditor } from 'lexical';
import papa from 'papaparse';
import { useCallback, useMemo, useState } from 'react';
import type { Attachment } from 'sr-types/lib/production/v1/graphql';
import { MATERIAL_COLUMN } from '../../production/helpers/productionImportUtils';
import { STATE_TYPE } from '../../production/productionConstants';
import { IG } from '../constants/constants';
import { AttachmentReferenceNode } from '../lexical/AttachmentReferenceNode';
import { lexicalTheme } from '../lexical/LexicalEditorTheme';
import type { Reference } from '../reference/reference';
import { useStyling } from '../Theme';
import { failedToastMessage, generateTempId } from '../utils/commonUtils';
import upload, { createLinkFormData, UPLOAD_URL } from '../utils/upload';
import type {
    AttachmentDataItem,
    ImportRow,
    UseImportPreviewDataParams,
    UseImportPreviewDataResult
} from './types';
import { validateImportLookupValue } from './utils/SRDataImportUtils';

const createAttachment = (id: string, label: string, isLink: boolean, reference: Reference | null): Attachment => ({
    id,
    name: label,
    isLink,
    type: 'import',
    entityReference: reference ?? null
});

const updateAttachmentsWithUploadReferences = (
    attachmentsByRow: Map<string, Attachment[]>,
    referenceByFileName: Map<string, Reference | undefined>
): Map<string, Attachment[]> => {
    const updatedAttachmentsByRow = new Map<string, Attachment[]>();

    attachmentsByRow.forEach((attachments, rowId) => {
        const updatedAttachments = attachments.map((attachment) => {
            const reference = referenceByFileName.get(attachment.name?.trim());

            if (!reference || attachment.entityReference?.id) {
                return attachment;
            }

            return {
                ...attachment,
                entityReference: reference
            };
        });

        updatedAttachmentsByRow.set(rowId, updatedAttachments);
    });

    return updatedAttachmentsByRow;
};

const updateRowsWithAttachments = (rows: ImportRow[], attachmentsByRow: Map<string, Attachment[]>): ImportRow[] => {
    const updatedRows = rows.map((row) => {
        const attachments = attachmentsByRow.get(row.id);

        if (!attachments) {
            return row;
        }

        return {
            ...row,
            [MATERIAL_COLUMN]: JSON.stringify(attachments)
        };
    });

    return updatedRows;
};

export function useImportPreviewData({
    activeOrganizationAccount,
    columnPreferences,
    defaultColumns,
    externalColumnVisibilityModel,
    hasDataToImport,
    hasAIDataToImport,
    hasHeaders,
    state,
    text,
    isNotReady = false,
    predefinedValuesByColumn
}: UseImportPreviewDataParams): UseImportPreviewDataResult {
    const [rows, setRows] = useState<ImportRow[]>([]);
    const [selectedRows, setSelectedRows] = useState<ImportRow[]>([]);
    const [materialAttachmentData, setMaterialAttachmentData] = useState<Map<string, Attachment[]>>(new Map());
    const [fileTobeUploaded, setFileTobeUploaded] = useState<string[]>([]);
    const { theme } = useStyling();

    /** Create an lexical headless editor in order to convert imported pipeline materials to attachment-reference HTML. */
    const attachmentReferenceEditor = useMemo<LexicalEditor | null>(() => {
        if (state === STATE_TYPE.pipeline) {
            return createEditor({
                namespace: 'AttachmentReferenceImport',
                nodes: [AttachmentReferenceNode],
                theme: lexicalTheme(theme)
            });
        }
        return null;
    }, [state, theme]);

    /** Parse material cells, reusing existing references and uploading newly supplied links. */
    const parseMaterials = useCallback(
        async (value: string, rowId: string): Promise<Attachment[]> => {
            if (!value?.trim()) {
                return [];
            }

            const items = value
                .split('|')
                .map((item) => item.trim())
                .filter(Boolean);

            const fileTobeUploaded: string[] = [];

            const uploadedLink: AttachmentDataItem[] = [];

            const materialPromises: Promise<Attachment | null>[] = items.map(async (item) => {
                const colonIndex = item.indexOf(':');

                const label = (colonIndex === -1 ? item : item.slice(0, colonIndex)).trim();

                const link = colonIndex === -1 ? null : item.slice(colonIndex + 1).trim();

                if (!label) {
                    return null;
                }

                // No link: create an attachment that needs to be uploaded later.
                if (!link) {
                    const newAttachment = createAttachment(IG + generateTempId(), label, false, null);
                    if (!fileTobeUploaded.includes(label)) {
                        fileTobeUploaded.push(label);
                    }
                    return newAttachment;
                }

                const alreadyUploaded = uploadedLink.find(
                    (linkObj) => linkObj.label === label && linkObj.link === link
                );

                if (alreadyUploaded) {
                    return createAttachment(
                        IG + generateTempId(),
                        alreadyUploaded.label,
                        true,
                        alreadyUploaded.reference
                    );
                }

                try {
                    const response = await upload(
                        activeOrganizationAccount,
                        UPLOAD_URL,
                        undefined,
                        createLinkFormData(link, label)
                    );

                    const reference = response?.data?.reference;

                    if (!reference) {
                        throw new Error('Upload response did not contain a reference');
                    }

                    uploadedLink.push({
                        label,
                        link,
                        reference
                    });

                    return createAttachment(IG + generateTempId(), label, true, reference);
                } catch {
                    failedToastMessage('import.material.file.upload.error', { fileName: label });

                    return null;
                }
            });

            const results = await Promise.all(materialPromises);
            setFileTobeUploaded((previousFiles) => [...new Set([...previousFiles, ...fileTobeUploaded])]);
            return results.filter((attachment): attachment is Attachment => attachment !== null);
        },
        [activeOrganizationAccount]
    );

    // Convert the import text into preview rows and resolve pipeline material references.
    const createPreview = useCallback(
        async (hasHeadersInTable = false, csvText = undefined) => {
            if (isNotReady) {
                return;
            }
            setRows([]);
            if (!csvText && !text) {
                return;
            }
            const textToParse = csvText ?? text;
            const parsed = papa.parse(textToParse, { skipEmptyLines: false });
            const lines = [...parsed.data];
            if (hasHeadersInTable || hasHeaders || hasAIDataToImport) {
                lines.shift();
            }

            const columns = hasDataToImport
                ? defaultColumns
                : columnPreferences
                  ? defaultColumns.filter((column) => columnPreferences[column?.field] !== false)
                  : defaultColumns;

            const visibleColumns = columns.filter((column) => externalColumnVisibilityModel[column.field] !== false);
            const previewRows = lines.map((line) => {
                const row: ImportRow = {};
                visibleColumns.forEach((column, index) => {
                    row[column.field] = validateImportLookupValue(
                        column.field,
                        line[index]?.trim(),
                        predefinedValuesByColumn
                    );
                });

                /** Set id AFTER mapping to prevent it from being overwritten by a column named "id". */
                row.id = IG + generateTempId();
                return row;
            });

            if (state === STATE_TYPE.pipeline && attachmentReferenceEditor) {
                await Promise.all(
                    previewRows.map(async (row) => {
                        if (!row[MATERIAL_COLUMN]) {
                            return;
                        }
                        const attachments = await parseMaterials(row[MATERIAL_COLUMN], row.id);
                        if (attachments.length > 0) {
                            const attachmentString = JSON.stringify(attachments);
                            setMaterialAttachmentData((rowAttachments) => {
                                const next = new Map(rowAttachments);
                                next.set(row.id, attachments);
                                return next;
                            });
                            row[MATERIAL_COLUMN] = attachmentString;
                        }
                    })
                );
            }
            setRows(previewRows);
        },
        [attachmentReferenceEditor, columnPreferences, defaultColumns, externalColumnVisibilityModel, hasAIDataToImport, hasDataToImport, hasHeaders, isNotReady, parseMaterials, predefinedValuesByColumn, state, text]
    );

    // Apply uploaded file references and refresh HTML only for their affected preview rows.
    const handleFileUploadChange = useCallback((fileArray: File[], references: AttachmentDataItem['reference'][]) => {
        if (!fileArray?.length || !references?.length) {
            return;
        }

        const referenceByFileName = new Map(fileArray.map((file, index) => [file.name.trim(), references[index]]));

        setMaterialAttachmentData((previous) => {
            const updatedData = updateAttachmentsWithUploadReferences(previous, referenceByFileName);

            setRows((previousRows) => updateRowsWithAttachments(previousRows, updatedData));
            setSelectedRows((previousRows) => updateRowsWithAttachments(previousRows, updatedData));

            return updatedData;
        });

        setFileTobeUploaded((previousFiles) => {
            const uploadedFileNames = new Set(fileArray.map((file) => file.name.trim()));

            return previousFiles.filter((fileName) => !uploadedFileNames.has(fileName?.trim()));
        });
    }, []);

    /** Handles the removal of an attachment from the import preview. */
    const handleAttachmentRemoveFromImport = useCallback((attachment: Attachment) => {
        setMaterialAttachmentData((previous) => {
            const updatedData = new Map(previous);

            updatedData.forEach((attachments, rowId) => {
                const updatedAttachments = attachments.filter((item) => item.id !== attachment.id);

                updatedData.set(rowId, updatedAttachments);
            });

            return updatedData;
        });

        setFileTobeUploaded((previous) => previous.filter((item) => item !== attachment.name));
    }, []);

    /** Attachments still pending an upload are excluded until their reference resolves. */
    const getSelectedAttachments = useCallback((): Map<string, Attachment[]> => {
        const selectedRowIds = new Set(selectedRows.map((row) => String(row.id)));

        if (!selectedRowIds.size || !materialAttachmentData.size) {
            return new Map();
        }

        const selectedAttachments = new Map<string, Attachment[]>();

        materialAttachmentData.forEach((attachments, rowId) => {
            if (!selectedRowIds.has(String(rowId))) {
                return;
            }

            const attachmentsWithReference = attachments.filter((attachment) => attachment?.entityReference);

            if (attachmentsWithReference.length) {
                selectedAttachments.set(rowId, attachmentsWithReference);
            }
        });

        return selectedAttachments;
    }, [selectedRows, materialAttachmentData]);

    const isEveryFileUploaded = useCallback((): boolean => {
        const selectedRowIds = new Set(selectedRows.map((row) => row.id));
        const allFilesUploaded = ![...materialAttachmentData.entries()].some(
            ([rowId, attachments]) =>
                selectedRowIds.has(rowId) && attachments.some((attachment) => !attachment.isLink && !attachment.entityReference)
        );
        return allFilesUploaded;
    }, [materialAttachmentData, selectedRows]);

    const handleDelete = useCallback((id: string) => {
        setSelectedRows((previous) => previous.filter((row) => row.id !== id));
    }, []);

    return {
        attachmentReferenceEditor,
        createPreview,
        fileTobeUploaded,
        isEveryFileUploaded,
        getSelectedAttachments,
        handleDelete,
        handleFileUploadChange,
        handleAttachmentRemoveFromImport,
        materialAttachmentData,
        rows,
        selectedRows,
        setRows,
        setSelectedRows
    };
}
